import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged 
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { app } from '../firebase-plus';

const auth = getAuth(app);
const db = getFirestore(app);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);

  // Check and reset credits if billing date has passed
  const checkAndResetCredits = async (userId, userDocData) => {
    if (!userDocData || !userDocData.subscription) {
      return userDocData;
    }

    const subscription = userDocData.subscription;
    const nextBillingDate = subscription.nextBillingDate;

    if (!nextBillingDate) {
      return userDocData;
    }

    // Convert Firestore Timestamp to Date if needed
    let billingDate;
    if (nextBillingDate.toDate) {
      billingDate = nextBillingDate.toDate();
    } else if (nextBillingDate instanceof Date) {
      billingDate = nextBillingDate;
    } else {
      billingDate = new Date(nextBillingDate);
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const billingDateOnly = new Date(billingDate.getFullYear(), billingDate.getMonth(), billingDate.getDate());

    // Check if we've already reset today to avoid multiple resets
    const lastResetDate = subscription.lastResetDate;
    let alreadyResetToday = false;
    if (lastResetDate) {
      const lastReset = lastResetDate.toDate ? lastResetDate.toDate() : new Date(lastResetDate);
      const lastResetOnly = new Date(lastReset.getFullYear(), lastReset.getMonth(), lastReset.getDate());
      alreadyResetToday = lastResetOnly.getTime() === today.getTime();
    }

    // Check if billing date has passed (today >= billing date) and we haven't reset today
    if (today >= billingDateOnly && !alreadyResetToday) {
      // Calculate next billing date (add 1 month)
      const newBillingDate = new Date(billingDate);
      newBillingDate.setMonth(newBillingDate.getMonth() + 1);

      // Get subscription plan credits
      const subscriptionPlans = {
        basic: 100,
        premium: 500,
        enterprise: 2000
      };
      const planCredits = subscriptionPlans[subscription.plan] || 0;

      // Reset credits to zero first, then add plan credits (effectively setting to plan amount)
      // This ensures unused credits from previous cycle are cleared
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        credits: planCredits, // Reset to plan amount (which is effectively 0 + plan credits)
        'subscription.nextBillingDate': Timestamp.fromDate(newBillingDate),
        'subscription.lastResetDate': Timestamp.now()
      });

      // Update local state
      const updatedUserData = {
        ...userDocData,
        credits: planCredits,
        subscription: {
          ...subscription,
          nextBillingDate: Timestamp.fromDate(newBillingDate),
          lastResetDate: Timestamp.now()
        }
      };

      console.log(`Credits reset for user ${userId}. New credits: ${planCredits}, Next billing date: ${newBillingDate.toLocaleDateString()}`);
      return updatedUserData;
    }

    return userDocData;
  };

  // Load user data from Firestore
  const loadUserData = async (user) => {
    if (!user) {
      setUserData(null);
      return;
    }

    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        let userData = userSnap.data();
        
        // If subscription is not in userData, try to get it from subscriptions collection
        if (!userData.subscription) {
          try {
            const subscriptionsRef = collection(db, 'subscriptions');
            const q = query(subscriptionsRef, where('userId', '==', user.uid), where('status', '==', 'active'));
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              const subDoc = querySnapshot.docs[0];
              const subscriptionData = subDoc.data();
              userData.subscription = subscriptionData;
              
              // Update user document with subscription reference
              await updateDoc(userRef, {
                subscription: subscriptionData
              });
            }
          } catch (subError) {
            console.error('Error loading subscription:', subError);
          }
        }
        
        // Check and reset credits if needed
        userData = await checkAndResetCredits(user.uid, userData);
        
        setUserData(userData);
      } else {
        // Create user document if it doesn't exist
        const newUserData = {
          email: user.email,
          credits: 0,
          createdAt: Timestamp.now(),
          subscription: null
        };
        await setDoc(userRef, newUserData);
        setUserData(newUserData);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      setUserData(null);
    }
  };

  // Check if user is coming from raptorock domain
  const isFromRaptorockDomain = () => {
    if (typeof window === 'undefined') return false;
    
    // Check URL parameter first (most reliable)
    const urlParams = new URLSearchParams(window.location.search);
    const fromRaptorock = urlParams.get('from') === 'raptorock' || urlParams.get('ref') === 'raptorock';
    if (fromRaptorock) {
      // Store in sessionStorage for this session
      sessionStorage.setItem('fromRaptorock', 'true');
      return true;
    }
    
    // Check sessionStorage (set from previous check or URL param)
    if (sessionStorage.getItem('fromRaptorock') === 'true') {
      return true;
    }
    
    // Check localStorage (persists across sessions)
    if (localStorage.getItem('fromRaptorock') === 'true') {
      return true;
    }
    
    // Check current origin
    const origin = window.location.origin;
    const referrer = document.referrer;
    
    // Check if current origin or referrer is from raptorock
    const raptorockDomains = [
      'raptorock-f0b08.web.app',
      'raptorock-f0b08.firebaseapp.com'
    ];
    
    const isRaptorockOrigin = raptorockDomains.some(domain => 
      origin.includes(domain) || referrer.includes(domain)
    );
    
    if (isRaptorockOrigin) {
      // Store flag for future checks
      sessionStorage.setItem('fromRaptorock', 'true');
      localStorage.setItem('fromRaptorock', 'true');
      return true;
    }
    
    return false;
  };

  // Sign up
  const signup = async (email, password) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Check if coming from raptorock domain for bonus credits
    const fromRaptorock = isFromRaptorockDomain();
    const initialCredits = fromRaptorock ? 10000 : 0;

    // Create user document in Firestore
    const userRef = doc(db, 'users', user.uid);
    await setDoc(userRef, {
      email: user.email,
      credits: initialCredits,
      createdAt: Timestamp.now(),
      subscription: null,
      ...(fromRaptorock && { 
        raptorockBonus: true,
        raptorockBonusDate: Timestamp.now()
      })
    });

    if (fromRaptorock) {
      console.log(`Raptorock signup bonus: Allocated ${initialCredits} credits to ${user.email}`);
      // Clear the flag from storage after bonus is allocated
      sessionStorage.removeItem('fromRaptorock');
      localStorage.removeItem('fromRaptorock');
    }

    await loadUserData(user);
    return userCredential;
  };

  // Sign in
  const login = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Check if coming from raptorock domain and if user hasn't received bonus yet
    const fromRaptorock = isFromRaptorockDomain();
    
    if (fromRaptorock) {
      try {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          
          // If user hasn't received raptorock bonus yet, allocate it
          if (!userData.raptorockBonus) {
            const currentCredits = userData.credits || 0;
            const newCredits = currentCredits + 10000;
            
            await updateDoc(userRef, {
              credits: newCredits,
              raptorockBonus: true,
              raptorockBonusDate: Timestamp.now()
            });
            
            // Clear the flag from storage after bonus is allocated
            sessionStorage.removeItem('fromRaptorock');
            localStorage.removeItem('fromRaptorock');
            
            console.log(`Raptorock signin bonus: Allocated 10000 credits to ${user.email}. Total: ${newCredits}`);
          } else {
            // User already received bonus, clear flag
            sessionStorage.removeItem('fromRaptorock');
            localStorage.removeItem('fromRaptorock');
          }
        }
      } catch (error) {
        console.error('Error allocating raptorock bonus on signin:', error);
      }
    }

    await loadUserData(user);
    return userCredential;
  };

  // Sign out
  const logout = async () => {
    await signOut(auth);
    setUserData(null);
  };

  // Deduct credits
  const deductCredits = async (amount) => {
    if (!currentUser || !userData) {
      throw new Error('User not authenticated');
    }

    const newCredits = Math.max(0, (userData.credits || 0) - amount);
    
    const userRef = doc(db, 'users', currentUser.uid);
    await updateDoc(userRef, {
      credits: newCredits
    });

    setUserData({ ...userData, credits: newCredits });
  };

  // Check if user has enough credits
  const hasEnoughCredits = (required = 10) => {
    if (!userData) return false;
    return (userData.credits || 0) >= required;
  };

  // Monitor auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await loadUserData(user);
      } else {
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Periodically check for billing date (every hour)
  useEffect(() => {
    if (!currentUser || !userData) return;

    const checkBillingInterval = setInterval(async () => {
      await loadUserData(currentUser);
    }, 60 * 60 * 1000); // Check every hour

    return () => clearInterval(checkBillingInterval);
  }, [currentUser, userData]);

  const value = {
    currentUser,
    userData,
    signup,
    login,
    logout,
    deductCredits,
    hasEnoughCredits,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

